import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { BaseComponent } from 'src/shared/abstracts/base.abstract';
import { featuresWithPermissions } from 'src/shared/data/permissions';
import { BasicInputTypeEnum } from 'src/shared/enums/basic-input-type.enum';
import { IFilterCheckboxGroup, IFilterConfig } from 'src/shared/interfaces/filters.interface';
import { ISelectChoices } from 'src/shared/interfaces/generic-select-config.interface';

@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
})
export class FilterComponent extends BaseComponent implements OnInit {
    @Input() public filterConfig: IFilterConfig;
    @Output() public applyFiltersEvent = new EventEmitter<any>();

    public filterForm: FormGroup;

    public isDateRangeInError = false;
    public showFilter = false;
    public permissions = featuresWithPermissions;
    public basicInputTypeEnum = BasicInputTypeEnum;

    // GETTERS

    public get startDate(): FormControl {
        if (!this.filterConfig.hasDateRange) return null;
        return this.filterForm.get('dateStart') as FormControl;
    }

    public get endDate(): FormControl {
        if (!this.filterConfig.hasDateRange) return null;
        return this.filterForm.get('dateEnd') as FormControl;
    }

    public get search(): FormControl {
        return this.filterForm.get('search') as FormControl;
    }

    public getControl(id: string): FormControl {
        return this.filterForm.get(id) as FormControl;
    }

    constructor(private fb: FormBuilder) {
        super();
    }

    ngOnInit(): void {
        this.createFilterForm();
    }

    public emitFilters(): void {
        this.applyFiltersEvent.emit(this.filterForm.getRawValue());
    }

    public resetFilter(): void {
        if (!this.filterConfig.checkboxGroups.some((c) => c.checkAllChildrenByDefault)) {
            this.showFilter = false;
        }

        this.filterForm.patchValue(
            {
                search: '',
                dateStart: this.filterConfig.dateRangeStart,
                dateEnd: this.filterConfig.dateRangeEnd,
            },
            { emitEvent: false }
        );
        if (this.filterConfig.checkboxGroups?.length > 0) {
            this.filterConfig.checkboxGroups.forEach((checkboxGroup: IFilterCheckboxGroup) => {
                this.filterForm.get(checkboxGroup.id).patchValue([]);
                if (checkboxGroup.child?.length) {
                    checkboxGroup.child.forEach((child: IFilterCheckboxGroup) => {
                        this.filterForm.get(child.id).patchValue([]);
                        if (child.child?.length) {
                            child.child.forEach((c: IFilterCheckboxGroup) => {
                                this.filterForm.get(c.id).patchValue([]);
                            });
                        }
                    });
                }
            });
        }

        this.emitFilters();

        if (this.filterConfig.checkboxGroups.some((c) => c.checkAllChildrenByDefault)) {
            this.createFilterForm();
        }
    }

    public isDateRangeValid(): boolean {
        if (!this.filterConfig.hasDateRange) return true;
        if (this.startDate.value && this.endDate.value) {
            this.isDateRangeInError = new Date(this.startDate.value).getTime() > new Date(this.endDate.value).getTime();
        }
        return !this.isDateRangeInError;
    }

    private createFilterForm(): void {
        this.destroyLocalSubs();
        // create search
        this.filterForm = this.fb.group({
            search: [''],
        });
        // create date range
        if (this.filterConfig.hasDateRange) {
            this.filterForm.addControl(
                'dateStart',
                new FormControl(this.filterConfig.dateRangeStart ? this.filterConfig.dateRangeStart : '')
            );
            this.filterForm.addControl('dateEnd', new FormControl(this.filterConfig.dateRangeEnd ? this.filterConfig.dateRangeEnd : ''));
        }
        // create checkbox groups
        if (this.filterConfig.checkboxGroups?.length > 0) {
            // level 1
            this.filterConfig.checkboxGroups.forEach((checkboxGroup: IFilterCheckboxGroup) => {
                this.filterForm.addControl(checkboxGroup.id, new FormControl([]));
                if (checkboxGroup.checkAllChildrenByDefault) {
                    this.filterForm.get(checkboxGroup.id).setValue(checkboxGroup.options.map((o: ISelectChoices) => o.value));
                }
                // level 2
                checkboxGroup.child?.forEach((child1: IFilterCheckboxGroup) => {
                    this.filterForm.addControl(child1.id, new FormControl([]));
                    if (child1.checkAllChildrenByDefault) {
                        this.filterForm.get(child1.id).setValue(child1.options.map((o: ISelectChoices) => o.value));
                    }
                    // level 3
                    child1.child?.forEach((child2: IFilterCheckboxGroup) => {
                        this.filterForm.addControl(child2.id, new FormControl([]));
                        if (child2.checkAllChildrenByDefault) {
                            this.filterForm.get(child2.id).setValue(child2.options.map((o: ISelectChoices) => o.value));
                        }
                    });
                    if (child1.checkChildrenOnCheck) {
                        this.checkChildrenOnParentChange(child1.id, child1.child);
                    }
                });
                if (checkboxGroup.checkChildrenOnCheck) {
                    this.checkChildrenOnParentChange(checkboxGroup.id, checkboxGroup.child);
                }
            });
        }

        this.initSearchSub();
    }

    private initSearchSub(): void {
        this.localUns = this.search.valueChanges.pipe(debounceTime(500)).subscribe(() => {
            this.emitFilters();
        });

        if (this.filterConfig.hasDateRange) {
            this.localUns = this.startDate.valueChanges.pipe(debounceTime(500)).subscribe(() => {
                if (this.isDateRangeValid()) {
                }
            });

            this.localUns = this.endDate.valueChanges.pipe(debounceTime(500)).subscribe(() => {
                if (this.isDateRangeValid()) {
                }
            });
        }
    }

    // Check les checkbox enfants du parent si le parent est checked
    private checkChildrenOnParentChange(parentId: string, children: IFilterCheckboxGroup[]): void {
        this.localUns = this.filterForm.get(parentId).valueChanges.subscribe((v) => {
            if (!v) {
                return;
            }
            if (v[0]) {
                children.forEach((c) => {
                    this.filterForm.get(c.id).patchValue(c.options.map((o) => o.value));
                });
            }
        });
    }
}
